import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ShieldCheck, Wrench, Flame, Check, ChevronLeft, X, FileText } from 'lucide-react'
import { getCar, brl, km, STATUS } from '../data.js'

function PropostaModal({ car, plano, onClose }) {
  const navigate = useNavigate()
  const [etapa, setEtapa] = useState('cadastro') // cadastro -> sucesso
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 sm:items-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-2xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl sm:rounded-2xl sm:p-6" onClick={(e) => e.stopPropagation()}>
        {etapa === 'cadastro' ? (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl font-bold">Enviar proposta</h3>
                <p className="mt-1 text-sm text-slate-500">Cadastro rápido pra gente te conhecer.</p>
              </div>
              <button onClick={onClose} className="rounded-full p-2 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="mt-4 rounded-xl bg-brand-50 p-4 text-sm">
              <div className="font-semibold text-brand-800">{car.nome}</div>
              <div className="mt-0.5 text-brand-700">
                Entrada {brl(plano.entrada)} · {plano.parcelas}x de <b>{brl(plano.valor)}</b>/mês
              </div>
            </div>
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => { e.preventDefault(); setEtapa('sucesso') }}
            >
              <input required placeholder="Nome completo" className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-base outline-none sm:text-sm focus:border-brand-600" />
              <input required placeholder="WhatsApp (DDD + número)" className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-base outline-none sm:text-sm focus:border-brand-600" />
              <input type="email" placeholder="E-mail (opcional)" className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-base outline-none sm:text-sm focus:border-brand-600" />
              <textarea placeholder="Mensagem para a Sim (opcional)" rows={2} className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-base outline-none sm:text-sm focus:border-brand-600" />
              <button className="w-full rounded-xl bg-brand-600 py-3 font-semibold text-white transition hover:bg-brand-700">
                Enviar proposta
              </button>
              <p className="text-center text-[11px] text-slate-400">Sem compromisso — a proposta passa por análise da Sim.</p>
            </form>
          </>
        ) : (
          <div className="text-center">
            <div className="mx-auto inline-flex rounded-full bg-brand-50 p-4 text-brand-600"><Check size={32} /></div>
            <h3 className="font-display mt-4 text-xl font-bold">Proposta enviada! 🎉</h3>
            <p className="mt-2 text-sm text-slate-500">
              Agora acelere sua análise: envie seus documentos e a gente te liga no WhatsApp.
            </p>
            <button
              onClick={() => navigate('/documentos')}
              className="mt-5 w-full rounded-xl bg-brand-600 py-3 font-semibold text-white transition hover:bg-brand-700"
            >
              Enviar documentos agora
            </button>
            <button onClick={onClose} className="mt-2 w-full rounded-xl py-2.5 text-sm font-medium text-slate-500 hover:bg-slate-50">
              Deixar para depois
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default function Carro() {
  const { id } = useParams()
  const car = getCar(id)
  const [foto, setFoto] = useState(0)
  const [plano, setPlano] = useState(car?.planos[0]?.id)
  const [modal, setModal] = useState(false)

  if (!car) return <main className="mx-auto max-w-6xl px-4 py-20 text-center text-slate-500">Carro não encontrado.</main>

  const planoSel = car.planos.find((p) => p.id === plano)
  const disponivel = car.status === 'disponivel'
  const st = STATUS[car.status]

  return (
    <main className="mx-auto max-w-6xl px-4 py-5 pb-28 md:py-8 lg:pb-8">
      <Link to="/carros" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-brand-700">
        <ChevronLeft size={16} /> Voltar para o catálogo
      </Link>

      <div className="mt-4 grid gap-6 md:gap-8 lg:grid-cols-[1.4fr_1fr]">
        {/* Coluna esquerda */}
        <div>
          <div className="overflow-hidden rounded-2xl">
            <img src={car.fotos[foto]} alt={car.nome} className="aspect-[16/10] w-full object-cover" />
          </div>
          {car.fotos.length > 1 && (
            <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
              {car.fotos.map((f, i) => (
                <button key={f} onClick={() => setFoto(i)} className={`shrink-0 overflow-hidden rounded-xl border-2 ${i === foto ? 'border-brand-600' : 'border-transparent opacity-70'}`}>
                  <img src={f} alt="" className="h-16 w-24 object-cover md:h-20 md:w-28" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">{car.nome}</h1>
            {car.consignado && <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-600">Consignado</span>}
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${st.cls}`}>{st.label}</span>
          </div>
          <div className="mt-1 text-slate-500">{car.ano} · {km(car.kmRodados)} · {car.cambio} · {car.combustivel}</div>

          <div className="mt-4 flex items-center gap-3 rounded-2xl bg-brand-50 p-4 text-sm text-brand-800 md:mt-5">
            <div className="flex gap-2"><ShieldCheck size={20} /><Wrench size={20} /></div>
            <div><b>Seguro e manutenção por conta da Sim.</b> Você só se preocupa em dirigir.</div>
          </div>

          <h2 className="font-display mt-6 text-lg font-bold md:mt-8 md:text-xl">Ficha técnica</h2>
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
            {[
              ['Carroceria', car.carroceria], ['Portas', car.portas], ['Motor', car.motor],
              ['Cor', car.cor], ['Final da placa', car.placaFinal], ['Único dono', car.unicoDono ? 'Sim' : 'Não'],
              ['Tabela FIPE', brl(car.fipe)], ['IPVA', 'Pago'], ['Licenciamento', 'Em dia'],
              ['Laudo cautelar', 'Aprovado'],
            ].map(([t, v]) => (
              <div key={t} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 md:px-4 md:py-3">
                <dt className="text-xs text-slate-400">{t}</dt>
                <dd className="mt-0.5 font-semibold text-slate-800">{v}</dd>
              </div>
            ))}
          </dl>

          <h2 className="font-display mt-6 text-lg font-bold md:mt-8 md:text-xl">Opcionais</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {car.opcionais.map((o) => (
              <span key={o} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600">{o}</span>
            ))}
          </div>
        </div>

        {/* Painel de proposta */}
        <div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-lg shadow-slate-900/5 md:p-6 lg:sticky lg:top-24">
            {car.propostas > 0 && (
              <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-700">
                <Flame size={14} /> Este carro tem {car.propostas} proposta{car.propostas > 1 ? 's' : ''}
              </div>
            )}
            <div className="text-sm text-slate-400">a partir de</div>
            <div className="font-display text-2xl font-extrabold text-brand-700 md:text-3xl">
              {brl(Math.min(...car.planos.map((p) => p.valor)))}<span className="text-base font-semibold text-slate-400">/mês</span>
            </div>

            {disponivel ? (
              <>
                <div className="mt-5 text-sm font-semibold text-slate-700">Escolha seu plano</div>
                <div className="mt-2 space-y-2">
                  {car.planos.map((p) => (
                    <label
                      key={p.id}
                      className={`flex cursor-pointer items-center justify-between rounded-xl border-2 px-4 py-3 transition ${
                        plano === p.id ? 'border-brand-600 bg-brand-50' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <input type="radio" name="plano" className="sr-only" checked={plano === p.id} onChange={() => setPlano(p.id)} />
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{p.parcelas}x de {brl(p.valor)}</div>
                        <div className="text-xs text-slate-500">Entrada de {brl(p.entrada)}</div>
                      </div>
                      {plano === p.id && <span className="rounded-full bg-brand-600 p-1 text-white"><Check size={14} /></span>}
                    </label>
                  ))}
                </div>
                <button
                  onClick={() => setModal(true)}
                  className="mt-5 w-full rounded-xl bg-brand-600 py-3.5 font-semibold text-white shadow-md shadow-brand-600/20 transition hover:bg-brand-700 active:scale-[0.98]"
                >
                  Enviar proposta
                </button>
                <p className="mt-3 text-center text-xs text-slate-400">
                  Termos finais combinados com a Sim após a análise. Visita e test drive após o envio dos documentos.
                </p>
              </>
            ) : (
              <div className="mt-5 rounded-xl bg-slate-100 p-4 text-sm text-slate-500">
                <FileText size={18} className="mb-2 text-slate-400" />
                Este carro está <b>{st.label.toLowerCase()}</b> e não aceita novas propostas no momento. Veja outros no catálogo — ou deixe seu contato que avisamos quando chegar um parecido.
              </div>
            )}
          </div>
        </div>
      </div>

      {disponivel && planoSel && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2.5 lg:hidden">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
            <div>
              <div className="text-[11px] text-slate-400">{planoSel.parcelas}x de</div>
              <div className="font-display text-lg font-extrabold leading-tight text-brand-700">{brl(planoSel.valor)}<span className="text-xs font-semibold text-slate-400">/mês</span></div>
            </div>
            <button onClick={() => setModal(true)} className="rounded-xl bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-brand-600/20 transition active:scale-[0.98]">
              Enviar proposta
            </button>
          </div>
        </div>
      )}

      {modal && planoSel && <PropostaModal car={car} plano={planoSel} onClose={() => setModal(false)} />}
    </main>
  )
}
